/**
 * Debug Logger — централизованное хранилище ошибок для отладки
 *
 * Собирает ошибки из:
 * - API (axios interceptor)
 * - JS (window.onerror)
 * - Promise (unhandledrejection)
 * - React (ErrorBoundary)
 */

const MAX_ENTRIES = 50;

// Хранилище ошибок
let errorLog = [];
let listeners = [];

// Типы ошибок
export const ErrorType = {
  API: 'API',
  JS: 'JS',
  REACT: 'React',
  PROMISE: 'Promise',
};

/**
 * Добавить ошибку в лог
 */
export const logError = (type, message, details = null) => {
  const entry = {
    id: Date.now() + Math.random(),
    timestamp: new Date().toLocaleTimeString('ru-RU'),
    type,
    message: String(message).substring(0, 200),
    details,
  };

  errorLog = [entry, ...errorLog].slice(0, MAX_ENTRIES);

  // Уведомляем слушателей
  listeners.forEach(fn => fn(errorLog));

  // Также пишем в консоль
  console.error(`[${type}] ${message}`, details || '');
};

/**
 * Получить все ошибки
 */
export const getErrors = () => errorLog;

/**
 * Очистить лог
 */
export const clearErrors = () => {
  errorLog = [];
  listeners.forEach(fn => fn(errorLog));
};

/**
 * Подписаться на изменения
 */
export const subscribe = (callback) => {
  listeners.push(callback);
  return () => {
    listeners = listeners.filter(fn => fn !== callback);
  };
};

/**
 * Проверяем, что ошибка от приложения, а не от расширения браузера
 */
const isAppError = (source) => {
  if (!source) return true;
  // Игнорируем ошибки от расширений
  if (source.includes('extension://')) return false;
  if (source.includes('emergent')) return false;
  return true;
};

/**
 * Инициализация глобальных обработчиков
 */
export const initGlobalErrorHandlers = () => {
  // JS ошибки (addEventListener — не перезапишется расширениями)
  window.addEventListener('error', (event) => {
    if (!isAppError(event.filename)) return;
    const location = event.filename ? `${event.filename.split('/').pop()}:${event.lineno}` : '';
    logError(ErrorType.JS, `${event.message} (${location})`, {
      source: event.filename,
      lineno: event.lineno,
      colno: event.colno,
      stack: event.error?.stack,
    });
  });

  // Необработанные Promise rejection
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    const message = reason?.message || String(reason);
    // Игнорируем ошибки от расширений
    if (message.includes('message channel closed') || message.includes('listener indicated')) return;
    logError(ErrorType.PROMISE, `Unhandled: ${message}`, {
      stack: reason?.stack,
    });
  });

  console.log('[DebugLogger] Global error handlers initialized');
};

/**
 * Логировать API ошибку
 */
export const logApiError = (method, url, status, message) => {
  logError(ErrorType.API, `${method} ${url} → ${status}: ${message}`, {
    method,
    url,
    status,
  });
};

/**
 * Логировать React ошибку
 */
export const logReactError = (error, componentStack) => {
  logError(ErrorType.REACT, error.message, {
    stack: error.stack,
    componentStack,
  });
};
